import { ApiError } from '@/api/http';

export type LoginErrorCode =
  | 'USER_NOT_FOUND'
  | 'WRONG_PASSWORD'
  | 'INVALID_EMAIL'
  | 'MISSING_EMAIL'
  | 'MISSING_PASSWORD'
  | 'ACCOUNT_PENDING_APPROVAL'
  | 'ACCOUNT_REMOVED'
  | 'UNKNOWN_ERROR';

export const LOGIN_ERROR_MESSAGES: Record<LoginErrorCode, string> = {
  USER_NOT_FOUND: 'No account found with that email.',
  WRONG_PASSWORD: 'Incorrect password.',
  INVALID_EMAIL: 'Enter a valid email address.',
  MISSING_EMAIL: 'Email is required.',
  MISSING_PASSWORD: 'Password is required.',
  ACCOUNT_PENDING_APPROVAL:
    "Your registration request is pending admin approval. You'll be able to sign in after your account is approved.",
  ACCOUNT_REMOVED:
    'Your account has been removed from the workspace. Contact support if you believe this is a mistake.',
  UNKNOWN_ERROR: 'Something went wrong. Please try again.',
};

type ApiErrorDetail = {
  code?: string;
  message?: string;
};

function readDetail(err: unknown): ApiErrorDetail | null {
  if (err instanceof ApiError && err.detail && typeof err.detail === 'object' && !Array.isArray(err.detail)) {
    return err.detail as ApiErrorDetail;
  }
  if (err && typeof err === 'object' && 'detail' in err) {
    const nested = (err as { detail?: unknown }).detail;
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      return nested as ApiErrorDetail;
    }
  }
  return null;
}

export function resolveLoginErrorCode(err: unknown): LoginErrorCode {
  const detail = readDetail(err);
  const code = (detail?.code || '').trim().toLowerCase();

  if (code === 'user_not_found') return 'USER_NOT_FOUND';
  if (code === 'wrong_password') return 'WRONG_PASSWORD';
  if (code === 'invalid_email') return 'INVALID_EMAIL';
  if (code === 'missing_email') return 'MISSING_EMAIL';
  if (code === 'missing_password') return 'MISSING_PASSWORD';
  if (code === 'account_pending_approval') return 'ACCOUNT_PENDING_APPROVAL';
  if (code === 'account_removed') return 'ACCOUNT_REMOVED';

  return 'UNKNOWN_ERROR';
}

export function mapLoginApiError(err: unknown): { code: LoginErrorCode; message: string } {
  const code = resolveLoginErrorCode(err);
  if (code === 'ACCOUNT_REMOVED') {
    const detail = readDetail(err);
    const message =
      typeof detail?.message === 'string' && detail.message.trim()
        ? detail.message.trim()
        : LOGIN_ERROR_MESSAGES.ACCOUNT_REMOVED;
    return { code, message };
  }
  if (code !== 'UNKNOWN_ERROR') {
    return { code, message: LOGIN_ERROR_MESSAGES[code] };
  }
  return { code: 'UNKNOWN_ERROR', message: LOGIN_ERROR_MESSAGES.UNKNOWN_ERROR };
}
